import { z } from 'zod';

import { HostCommissionType } from './hosts';
import { PositionSchema } from './position';
import { PriceTierSchema } from './price-tier-rules';
import { ShapeSchema } from './shape';
import { EmailStringSchema, LocalDateTimeStringSchema } from '../../common';
import { ExperienceKind, ExperienceVersion } from '../../experience/const';
import { buildQueryString } from '../../utils/querystring';
import { LanguageCode } from '../brand/brand';
import { BrandEmailNotificationSettingsSchema, BrandSmsNotificationSettingsSchema } from '../brand/settings';
import { makeEndpoint } from '../common';
import { IntegrationKeys, IntegrationName } from '../integration/integration';
import { ListingPictureKind } from '../media/media';
import { PaymentMethods } from '../payment-method/public';
import { TranslationSchema, TranslationWithIdSchema } from '../translation/types';

export const GetExperienceVersionQuerySchema = z.object({
  brandId: z.string().uuid().optional(),
  includeFirstAvailableDate: z.string().optional(),
});

export enum NotificationPlatform {
  EMAIL = 'email',
  SMS = 'sms',
}

export const ExperienceNotificationOverridesSchema = z.object({
  [NotificationPlatform.EMAIL]: BrandEmailNotificationSettingsSchema.optional(),
  [NotificationPlatform.SMS]: BrandSmsNotificationSettingsSchema.optional(),
});

const BaseTaxSchema = z.object({
  id: z.string().uuid(),
  includedInPrice: z.boolean(),
  isHostTax: z.boolean().optional(),
  order: z.number().int().min(0).optional().nullable(),
  showInAdvertisedPrice: z.boolean().optional().nullable(),
  appliesTo: z.array(z.string().uuid()).optional().nullable(),
});

export const PercentageTaxSchema = BaseTaxSchema.extend({
  percentage: z.number().min(0),
});
export type IPercentageTax = z.infer<typeof PercentageTaxSchema>;

export const AmountTaxSchema = BaseTaxSchema.extend({
  amount: z.number().min(0),
});
export type IAmountTax = z.infer<typeof AmountTaxSchema>;

export const TaxSchema = z.union([PercentageTaxSchema, AmountTaxSchema]);
export type Tax = z.infer<typeof TaxSchema>;

const CompoundBaseTaxSchema = z.object({
  id: z.string().uuid(),
  name: z.string().optional(),
  includedInPrice: z.boolean(),
  isHostTax: z.boolean().optional(),
  order: z.number().min(0).optional().nullable(),
  showInAdvertisedPrice: z.boolean().optional().nullable(),
  appliesTo: z.array(z.string()).optional().nullable(),
});

export type CompoundBaseTax = z.infer<typeof CompoundBaseTaxSchema>;
export const CompoundPercentageTaxSchema = CompoundBaseTaxSchema.extend({
  percentage: z.number().min(0),
});
export type ICompoundPercentageTax = z.infer<typeof CompoundPercentageTaxSchema>;

export const CompoundAmountTaxSchema = CompoundBaseTaxSchema.extend({
  amount: z.number().min(0),
});
export type ICompoundAmountTax = z.infer<typeof CompoundAmountTaxSchema>;

export const CompoundTaxSchema = z.union([CompoundPercentageTaxSchema, CompoundAmountTaxSchema]);
export type CompoundTax = z.infer<typeof CompoundTaxSchema>;

export const StrictTaxSchema = z.union([AmountTaxSchema.strict(), PercentageTaxSchema.strict()]);
export const StrictTaxWithNameSchema = z.union([
  AmountTaxSchema.extend({ name: z.string() }).strict(),
  PercentageTaxSchema.extend({ name: z.string() }).strict(),
]);

export enum ExternalFieldType {
  URL = 'url',
  EMAIL = 'email',
  PHONE = 'phone',
}

export const ExternalFieldSchema = z.object({
  type: z.nativeEnum(ExternalFieldType),
  value: z.string().nullable(),
});

export const ShouldValidateComplimentaryBookingSchema = z.object({
  roomCharge: z.boolean().optional(),
  memberCharge: z.boolean().optional(),
});

export enum BookingAvailabilityModeValue {
  PRIVATE_ONLY = 'private',
  GROUP_ONLY = 'group',
  NON_RESTRICTED = 'non-restricted',
}

export enum ExperienceType {
  RECURRING = 'recurring',
  ONE_TIME = 'one-time',
}

export const IntegrationMetaSchema = z.object({
  type: z.nativeEnum(IntegrationName),
  key: z.nativeEnum(IntegrationKeys),
  /** TransactionCode uses { code, description }; MewsTaxCodes uses string[] */
  value: z.union([z.record(z.unknown()), z.array(z.string())]),
});

export const UpdateExperienceRequestSchema = z.object({
  title: z.string().max(128, { message: 'Title must be at most 128 characters long.' }),
  headline: z.string().max(280).optional(),
  description: z.string().optional(),
  categoryId: z.string().uuid().optional(),
  vibeIds: z
    .array(z.string().uuid())
    .max(3, { message: 'A maximum of 3 vibes can be selected for an experience' })
    .refine((vibeIds) => new Set(vibeIds).size === vibeIds.length, { message: 'must be unique' })
    .optional(),
  locationId: z.string().uuid().optional(),
  location: z
    .object({
      latitude: z.number(),
      name: z.string(),
      longitude: z.number(),
      additionalInfo: z.string().optional(),
    })
    .optional(),
  minAge: z.number().int().min(1).max(150).optional(),
  maxAge: z.number().int().min(1).max(150).optional(),
  medias: z.array(z.object({ id: z.string().uuid(), kind: z.nativeEnum(ListingPictureKind) })).optional(),
  pictureIds: z
    .array(z.string().uuid())
    .max(10)
    .refine((pictureIds) => new Set(pictureIds).size === pictureIds.length, { message: 'must be unique' })
    .optional(),
  descriptionPictureId: z.string().uuid().optional().nullable(),
  bookingAvailabilityMode: z.nativeEnum(BookingAvailabilityModeValue).optional(),
  maxParticipantsCount: z.number().int().min(0).optional(),
  sessionDurationMinutes: z.number().int().min(1).optional(),
  isBookable: z.boolean(),
  isUnlisted: z.boolean().optional(),
  type: z.nativeEnum(ExperienceType).optional(),
  providedItems: z.array(z.string()).max(20).optional(),
  healthAndSafety: z.string().optional(),
  emailMessage: z.string().optional().nullable(),
  onlineBookingCutoffHours: z.number().max(1000).optional().nullable(),
  hostedById: z.string().uuid().optional(),
  hostCommission: z.number().optional(),
  hostCommissionType: z.nativeEnum(HostCommissionType).optional(),
  externalField: ExternalFieldSchema.optional(),
  paymentMethods: z.array(z.nativeEnum(PaymentMethods)).max(3).optional(),
  priceTiers: z.array(PriceTierSchema).min(1).optional(),
  taxes: z.array(TaxSchema).optional(),
  integrationMeta: z.array(IntegrationMetaSchema).min(1).optional(),
  partySize: z.number().int().optional().nullable(),
  hidePrice: z.boolean().optional(),
  isNoHost: z.boolean().optional(),
  isTicketed: z.boolean().optional(),
  autoUnpublishAt: LocalDateTimeStringSchema.optional().nullable(),
  notificationEmails: z.array(EmailStringSchema).max(10).optional().nullable(),
  notificationOverrides: ExperienceNotificationOverridesSchema.optional().nullable(),
  shouldValidateComplimentaryBooking: ShouldValidateComplimentaryBookingSchema.optional(),
  kind: z.nativeEnum(ExperienceKind).optional(),
  minimumRequiredToBook: z.number().int().min(1).optional(),
  maximumAllowedToBook: z.number().int().min(1).optional(),
  resourceGroupId: z.string().uuid().optional(),
  number: z.string().nonempty().optional(),
  displayedCapacity: z.number().optional(),
  shape: ShapeSchema.optional(),
  position: PositionSchema.optional(),
  cancellationPolicyId: z.string().uuid().optional().nullable(),
  reschedulePolicyId: z.string().uuid().optional().nullable(),
  limitedTimeId: z.string().optional().nullable(),
  requestToBook: z.boolean().optional(),
  customCallToAction: z.string().optional().nullable(),
  visibleOnCalendar: z.boolean().optional(),
});

export const CreateExperienceRequestSchema = UpdateExperienceRequestSchema.required({
  sessionDurationMinutes: true,
  location: true,
  customCallToAction: true,
}).extend({
  id: z.string().uuid().optional(),
  createdByAi: z.boolean().optional().default(false),
  aiExperienceId: z.string().uuid().optional(),
  isBookable: UpdateExperienceRequestSchema.shape.isBookable.default(true),
  type: UpdateExperienceRequestSchema.shape.type.default(ExperienceType.RECURRING),
  sessionDurationMinutes: z.number().int().min(1),
  location: z.object({
    name: z.string(),
    latitude: z.number(),
    longitude: z.number(),
    additionalInfo: z.string().optional(),
  }),
});

export const CreateExperienceResponseSchema = z.void();

export const CreateExperienceEndpoint = makeEndpoint({
  url: ({ brandId }: { brandId: string }) => `/v2/brands/${brandId}/experiences`,
  method: 'POST',
  requestSchema: CreateExperienceRequestSchema,
  responseSchema: CreateExperienceResponseSchema,
});

export const UpdateExperienceEndpoint = makeEndpoint({
  url: ({ brandId, experienceId }: { brandId: string; experienceId: string }) =>
    `/v1/brands/${brandId}/experiences/${experienceId}`,
  method: 'PATCH',
  requestSchema: UpdateExperienceRequestSchema,
  responseSchema: z.void(),
});

export const GetTranslationsForCategoriesQuerySchema = z.object({
  categoryId: z.string().uuid().optional(),
  language: z.nativeEnum(LanguageCode).optional(),
});

export const GetTranslationsForCategoriesEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId, query }: { brandId: string; query: z.input<typeof GetTranslationsForCategoriesQuerySchema> }) =>
    `/v1/brands/${brandId}/categories/i18n?${buildQueryString(query)}`,
  responseSchema: z.object({
    translations: z.array(TranslationSchema),
  }),
  requestSchema: z.void(),
  querySchema: GetTranslationsForCategoriesQuerySchema,
});

export const GetTranslationsForExperienceQuerySchema = z.object({
  language: z.nativeEnum(LanguageCode).optional(),
});

export const GetTranslationsForExperienceEndpoint = makeEndpoint({
  method: 'GET',
  url: ({
    experienceId,
    version,
    query,
  }: {
    experienceId: string;
    version: ExperienceVersion;
    query: z.input<typeof GetTranslationsForExperienceQuerySchema>;
  }) => `/v1/experiences/${experienceId}/version/${version}/i18n?${buildQueryString(query)}`,
  responseSchema: z.object({
    translations: z.object({
      experience: z.array(TranslationWithIdSchema),
      listingAddOns: z.array(TranslationWithIdSchema),
      customQuestions: z.array(TranslationWithIdSchema),
      cancellationPolicy: z.array(TranslationWithIdSchema),
      listingIncluded: z.array(TranslationWithIdSchema),
      location: z.array(TranslationWithIdSchema),
    }),
  }),
  requestSchema: z.void(),
  querySchema: GetTranslationsForExperienceQuerySchema,
});

export const PatchTranslationsForExperienceEndpoint = makeEndpoint({
  method: 'PATCH',
  url: ({ experienceId, version }: { experienceId: string; version: ExperienceVersion }) =>
    `/v1/experiences/${experienceId}/version/${version}/i18n`,
  responseSchema: z.void(),
  requestSchema: z.object({
    translations: z.array(TranslationSchema),
  }),
});

export const TranslateExperienceEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ experienceId }: { experienceId: string }) => `/v1/experiences/${experienceId}/translate`,
  responseSchema: z.void(),
  requestSchema: z.void(),
});

export enum ExperienceField {
  TITLE = 'title',
  HEADLINE = 'headline',
  DESCRIPTION = 'description',
  CUSTOM_CALL_TO_ACTION = 'customCallToAction',
}

export enum CategoryField {
  NAME = 'name',
}

export enum VibeField {
  NAME = 'name',
}

export enum CustomQuestionField {
  PROMPT = 'prompt',
}

export enum TaxField {
  NAME = 'name',
}

export const MigrateResourceToTimeBasedEndpoint = makeEndpoint({
  method: 'POST',
  url: () => '/v1/experiences/migrate/resource-to-timebased',
  responseSchema: z.void(),
  requestSchema: z.object({
    brandId: z.string(),
    resourceGroupId: z.string(),
    timeBasedExperienceId: z.string(),
    expEventFromTime: z.string().optional(),
    expEventToTime: z.string().optional(),
  }),
});
